/* eslint-disable no-nested-ternary */

import * as React from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { X, ChevronDown } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordian";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import QualificationQuestionIcon from '../../public/icons/new-dashboard/file-eye-02.svg';

/* ═════════ Types ═════════ */

type QuestionType = "integer" | "string" | "boolean" | "bool";

export type QualificationFilter = {
  capture_id: string;
  capture_output:
    | string[]
    | number[]
    | boolean
    | { min: number; max: number };
};

export interface QualificationQuestion {
  capture_id: string;
  qualification_question: string;
  return_type: QuestionType;
  capture_output:
    | string[]
    | number[]
    | boolean
    | { min: number; max: number };
}

interface Props {
  currentValue: any;
  qualificationQuestions: QualificationQuestion[];
  handleQuestionFilterUpdate: (
    qId: string,
    value: string | number | boolean | string[],
    type: "string" | "number_min" | "number_max" | "bool" | "reset",
  ) => void;
}

/* ═════════ Popper wrapper ═════════ */

export default function QualificationQuestionsPopper({
  currentValue,
  qualificationQuestions,
  handleQuestionFilterUpdate,
}: Props) {
  const [expanded, setExpanded] = React.useState<string>("");

  if (!qualificationQuestions.length) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 text-muted">
          <QualificationQuestionIcon />
          Qualification Questions
          {currentValue?.length > 0 && <div className="w-4 h-4 rounded-full bg-[#09090B] text-white text-xs">{currentValue?.length}</div>}
          <ChevronDown className="size-4" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 max-h-96 overflow-auto p-0">
        <Accordion type="single" collapsible>
          {qualificationQuestions.map((q) => {
            const saved = currentValue.find(c => c.capture_id === q.capture_id);
            return (
            <QuestionItem
              key={q.capture_id}
              question={q}
              overrideOutput={saved?.capture_output}
              expanded={expanded}
              setExpanded={setExpanded}
              onUpdate={handleQuestionFilterUpdate}
            />
          )})}
        </Accordion>
      </PopoverContent>
    </Popover>
  );
}

/* ═════════ Single Question row ═════════ */

interface ItemProps {
  question: QualificationQuestion;
  overrideOutput: any;
  expanded: string;
  setExpanded: (id: string) => void;
  onUpdate: (
    qId: string,
    value: string | number | boolean | string[],
    type: "string" | "number_min" | "number_max" | "bool" | "reset",
  ) => void;
}

function QuestionItem({
  question,
  overrideOutput,
  expanded,
  setExpanded,
  onUpdate,
}: ItemProps) {
   // use overrideOutput if present, else fall back to the question definition
   const captureOutput = overrideOutput ?? question.capture_output;

   /* --- local UI state, bootstrapped from captureOutput --- */
   const [badges, setBadges] = React.useState<string[]>(
     Array.isArray(captureOutput) && typeof captureOutput[0] === "string"
       ? (captureOutput as string[])
       : []
   );
 
    const [numMin, setNumMin] = React.useState<number | undefined>(() => {
       if (Array.isArray(captureOutput) && typeof captureOutput[0] === "number") {
         return (captureOutput as number[])[0];
       }
       if (
         captureOutput &&
         typeof captureOutput === "object" &&
         "min" in captureOutput
       ) {
         return (captureOutput as { min: number; max: number }).min;
       }
       return undefined;
     });
     const [numMax, setNumMax] = React.useState<number | undefined>(() => {
       if (Array.isArray(captureOutput) && typeof captureOutput[1] === "number") {
         return (captureOutput as number[])[1];
       }
       if (
         captureOutput &&
         typeof captureOutput === "object" &&
         "max" in captureOutput
       ) {
         return (captureOutput as { min: number; max: number }).max;
       }
       return undefined;
     });
 
   const [boolVal, setBoolVal] = React.useState<boolean | undefined>(() =>
     typeof captureOutput === "boolean" ? (captureOutput as boolean) : undefined
   );
 
   // any time overrideOutput changes, re-sync all three controls
   React.useEffect(() => {
     const out = overrideOutput ?? question.capture_output;
 
     if (question.return_type === "string") {
       setBadges(Array.isArray(out) ? (out as string[]) : []);
     } else if (question.return_type === "integer") {
           if (Array.isArray(out) && out.length === 2) {
              setNumMin((out as number[])[0]);
              setNumMax((out as number[])[1]);
             } else if (
               out &&
               typeof out === "object" &&
               "min" in out &&
               "max" in out
             ) {
              setNumMin((out as { min: number; max: number }).min);
              setNumMax((out as { min: number; max: number }).max);
             } else {
               setNumMin(undefined);
               setNumMax(undefined);
             }
     } else {
       setBoolVal(typeof out === "boolean" ? (out as boolean) : undefined);
     }
   }, [overrideOutput, question.capture_output, question.return_type]);

  /* --- handlers --- */
  const reset = () => {
    setBadges([]);
    setNumMin(undefined);
    setNumMax(undefined);
    setBoolVal(undefined);
    onUpdate(question.capture_id, [], "reset");
  };

  React.useEffect(() => {
    if (question.return_type === "string") {
      onUpdate(question.capture_id, badges, "string");
    }
  }, [badges]);

  /* --- render by type --- */
  let control: React.ReactNode = null;

  if (question.return_type === "integer") {
    control = (
      <div className="flex gap-2 max-w-full">
        <input
          type="number"
          placeholder="Min"
          value={numMin ?? ""}
          className="flex-1 border rounded px-2 py-2 text-sm w-1/2"
          onChange={(e) => {
            const v = Number(e.target.value);
            setNumMin(v);
            onUpdate(question.capture_id, v, "number_min");
          }}
        />
        <input
          type="number"
          placeholder="Max"
          value={numMax ?? ""}
          className="flex-1 border rounded px-2 py-2 text-sm w-1/2"
          onChange={(e) => {
            const v = Number(e.target.value);
            setNumMax(v);
            onUpdate(question.capture_id, v, "number_max");
          }}
        />
      </div>
    );
  } else if (question.return_type === "string") {
    control = (
      <BadgeInput
        badges={badges}
        setBadges={setBadges}
      />
    );
  } else {
    /* boolean */
    control = (
      <Select
        value={boolVal === undefined ? "" : boolVal ? "yes" : "no"}
        onValueChange={(v) => {
          const bool = v === "yes";
          setBoolVal(bool);
          onUpdate(question.capture_id, bool, "bool");
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="yes">Yes</SelectItem>
          <SelectItem value="no">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  const hasFilter =
    (question.return_type === "string" && badges.length > 0) ||
    (question.return_type === "integer" && (numMin !== undefined || numMax !== undefined)) ||
    (question.return_type !== "string" && question.return_type !== "integer" && boolVal !== undefined);

  return (
    <AccordionItem
      value={question.capture_id}
      className="border-none"
    >
      <AccordionTrigger
        onClick={() =>
          setExpanded(expanded === question.capture_id ? "" : question.capture_id)
        }
        className="flex justify-between items-center py-3 text-sm px-4"
      >
        <p className="text-left font-normal">{question.qualification_question}</p>
        <div className="flex items-center gap-2">
          {hasFilter && <span className="w-2 h-2 bg-secondary rounded-full" />}
          {hasFilter && (
            <X
              className="size-4 text-gray-500"
              onClick={(e) => {
                e.stopPropagation();
                reset();
              }}
            />
          )}
        </div>
      </AccordionTrigger>

      <AccordionContent className="p-4 pt-0 space-y-3">
        {control}
      </AccordionContent>
    </AccordionItem>
  );
}

/* ═════════ small badge input for string tags ═════════ */

function BadgeInput({
  badges,
  setBadges,
}: {
  badges: string[];
  setBadges: (b: string[]) => void;
}) {
  const [val, setVal] = React.useState("");

  const add = () => {
    if (val && !badges.includes(val)) {
      setBadges([...badges, val]);
      setVal("");
    }
  };

  return (
    <div className="flex flex-wrap gap-2 border rounded p-2">
      {badges.map((b) => (
        <Badge key={b} variant="secondary" className="gap-1 pl-2 pr-1">
          {b}
          <X
            className="size-3 ml-1 cursor-pointer"
            onClick={() => setBadges(badges.filter((x) => x !== b))}
          />
        </Badge>
      ))}

      <input
        value={val}
        placeholder="Add here"
        className="flex-1 text-sm border-none focus:outline-none"
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          } else if (e.key === "Backspace" && !val) {
            setBadges(badges.slice(0, -1));
          }
        }}
      />
    </div>
  );
}